import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../redux/store"; // Import RootState
import { AuthResponse, AuthState } from "./types/auth-types";
import { disconnectGoogle } from "./userSlice";

// Set initial state
const initialState: AuthState = {
  user: null,
  token: null,
  loading: false,
  error: null,
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// Google Login Thunk
export const loginGoogle = createAsyncThunk<
  AuthResponse,
  string,
  { rejectValue: string }
>("auth/loginGoogle", async (googleToken, thunkAPI) => {
  try {
    const response = await axios.post(`${API_URL}/auth/google`, {
      token: googleToken,
    });
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || "Google login failed",
    );
  }
});

// **Login Thunk**
export const login = createAsyncThunk<
  AuthResponse,
  { identifier: string; password: string },
  { rejectValue: string }
>("auth/login", async (credentials, thunkAPI) => {
  try {
    const response = await axios.post(`${API_URL}/login`, credentials);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || "Failed to login",
    );
  }
});

// **Signup Thunk**
export const signup = createAsyncThunk<
  AuthResponse,
  {
    email: string;
    password: string;
    username: string;
    firstname: string;
    lastname: string;
    othername?: string;
    phone_number: string;
    country: string;
    language: string;
  },
  { rejectValue: string }
>("auth/signup", async (credentials, thunkAPI) => {
  try {
    const response = await axios.post(`${API_URL}/signup`, credentials);
    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || "Failed to register",
    );
  }
});

// **ForgotPassword Thunk**
export const forgotPassword = createAsyncThunk<
  void,
  { email: string },
  { rejectValue: string }
>("auth/forgot-password", async ({ email }, thunkAPI) => {
  try {
    await axios.post(`${API_URL}/forgot-password`, { email });
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || "Failed to forgot password",
    );
  }
});

// **VerifyResetToken Thunk**
export const verifyResetToken = createAsyncThunk<
  void,
  { email: string; token: string },
  { rejectValue: string }
>("auth/verifyResetToken", async ({ email, token }, thunkAPI) => {
  try {
    await axios.post(`${API_URL}/verify-reset-token`, {
      email,
      token,
    });
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || "Invalid or expired reset token",
    );
  }
});

// **ResetPassword Thunk**
export const resetPassword = createAsyncThunk<
  void,
  {
    email: string;
    token: string;
    password: string;
    password_confirmation: string;
  },
  { rejectValue: string }
>(
  "auth/resetPassword",
  async ({ email, token, password, password_confirmation }, thunkAPI) => {
    try {
      await axios.post(`${API_URL}/reset-password`, {
        email,
        token,
        password,
        password_confirmation,
      });
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        error.response?.data?.message || "Failed to reset password",
      );
    }
  },
);

// **Change Password Thunk**
export const changePassword = createAsyncThunk<
  void,
  { oldPassword: string; newPassword: string },
  { rejectValue: string; state: RootState }
>("auth/changePassword", async ({ oldPassword, newPassword }, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.token;
    if (!token) return thunkAPI.rejectWithValue("Unauthorized");

    await axios.post(
      `${API_URL}/change-password`,
      { oldPassword, newPassword },
      { headers: { Authorization: `Bearer ${token}` } },
    );
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || "Failed to change password",
    );
  }
});

// **Change Email Thunk**
export const changeEmail = createAsyncThunk<
  { user: any }, // Success return type (updated user data)
  { email: string; password: string }, // Argument type
  { rejectValue: string; state: RootState }
>("auth/changeEmail", async ({ email, password }, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.token;
    if (!token) return thunkAPI.rejectWithValue("Unauthorized");

    const response = await axios.post(
      `${API_URL}/change-email`,
      { email, password },
      { headers: { Authorization: `Bearer ${token}` } },
    );

    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || "Failed to change email",
    );
  }
});

// **Change Username Thunk**
export const updatePersonalInfo = createAsyncThunk<
  { user: any },
  {
    username?: string;
    firstname?: string;
    lastname?: string;
    othername?: string;
    phone_number?: string;
    country?: string;
    language?: string;
  },
  { rejectValue: { message: string }; state: RootState }
>("auth/updatePersonalInfo", async (data, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.token;
    if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

    const response = await axios.put(
      `${process.env.REACT_APP_API_URL}/user`,
      data,
      { headers: { Authorization: `Bearer ${token}` } },
    );

    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data || {
        message: "Failed to update personal information",
      },
    );
  }
});

export const updateProfileImage = createAsyncThunk<
  { user: any },
  File,
  { rejectValue: { message: string }; state: RootState }
>("auth/updateProfileImage", async (imageFile, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.token;
    if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

    const formData = new FormData();
    formData.append("image", imageFile, imageFile.name);
    formData.append("_method", "PUT");

    const response = await axios.post(`${API_URL}/user`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data || { message: "Failed to update profile image" },
    );
  }
});

export const fetchCurrentUser = createAsyncThunk<
  { user: any },
  void,
  { rejectValue: string; state: RootState }
>("auth/fetchCurrentUser", async (_, thunkAPI) => {
  try {
    const token = thunkAPI.getState().auth.token;
    if (!token) return thunkAPI.rejectWithValue("Unauthorized");

    const response = await axios.get(`${API_URL}/user`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return response.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error.response?.data?.message || "Failed to fetch user",
    );
  }
});

// **Auth Slice**
const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.loading = false;
      state.error = null;
      // Clear token from storage
      localStorage.removeItem("token");
      sessionStorage.removeItem("token");
    },
  },
  extraReducers: (builder) => {
    builder
      // Google Login Cases
      .addCase(loginGoogle.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginGoogle.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(loginGoogle.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Google login failed";
      })
      // Login Cases
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to login";
      })

      // Signup Cases
      .addCase(signup.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(signup.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to register";
      })

      // Forgot Password Cases
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to register";
      })

      // Verify Reset Token Cases
      .addCase(verifyResetToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyResetToken.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(verifyResetToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Token verification failed";
      })

      // Reset Password Cases
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Password reset failed";
      })

      // Change Password Cases
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to change password";
      })

      // Change Email Cases
      .addCase(changeEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changeEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user; // Update user with new email
      })
      .addCase(changeEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to change email";
      })

      // Update Personal Info Cases
      .addCase(updatePersonalInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePersonalInfo.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.user) {
          state.user = action.payload.user;
        }
      })
      .addCase(updatePersonalInfo.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update personal information";
      })
      .addCase(updateProfileImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfileImage.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.user) {
          state.user = action.payload.user;
        }
      })
      .addCase(updateProfileImage.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update profile image";
      })
      .addCase(fetchCurrentUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.user) {
          state.user = action.payload.user;
        }
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch user";
      })

      .addCase(disconnectGoogle.fulfilled, (state, action) => {
        console.log("disconnectGoogle in authSlice:", action.payload);
        // Update the user in auth state
        if (state.user && action.payload.user) {
          state.user = action.payload.user; // Replace with updated user from backend
        }
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
